<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { ContentsContext, Feature, PageName } from '@/api/tracking';
import { PRODUCT_NAME } from '@/constants';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import DocumentTitle from '@/components/common/adp-document/basic-info/document-title.vue';
import CitationBadge from '@/components/common/atoms/citation-badge.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import { AdpDocument, isArticle, isResearchPaper } from '@/types';
import { useSurveyDocumentActions } from '@/utils/actions/useSurveyDocumentActions';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import {
  getPatentIdentifier,
  getPatentPublicationOffice,
  getPublisher,
  getSiteType,
  isPdfDocument,
} from '@/utils/adpDocument';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { imageErrorHandler } from '@/utils/errorHandler';
import { formatDate } from '@/utils/formatters';

interface Props {
  adpDocument: AdpDocument & { is_alliance_media?: boolean };
  badgeNumber: number;
  isNarrow: boolean; // 文字が溢れた際に`...`で表示するかどうかのフラグ
  /* これ以下は計測で使用 */
  pageName: PageName;
  feature: Feature;
  rankInWholeFeed: number; // 表示順位
  contentsContext: ContentsContext;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-document': [AdpDocument];
}>();

const { getCurrentPathForTracking } = useCurrentPathForTracking();

/**
 * Local States
 */
const { adpDocument } = toRefs(props);

const {
  actionModal,
  isPostingComment,
  isMarked,
  markedUsers,
  markedGroups,
  commentedUsers,
  optionActions,
  handleOptionAction,
  handleCommentButton,
  handleGroupMarkButton,
  handlePopupMenu,
  closeActionModal,
  handleMarkButton,
  groupMarkArticle,
  commentArticle,
} = useSurveyDocumentActions(
  adpDocument,
  {
    pageName: props.pageName,
    feature: props.feature,
    rankInWholeFeed: props.rankInWholeFeed,
    contentsContext: props.contentsContext,
  },
  emit,
);

const hitKeywords = computed(() => {
  const keywords = [
    ...(props.adpDocument?.hit_keywords ?? []),
    ...(props.adpDocument?.synonym_keywords ?? []),
  ].filter(keyword => keyword.length > 0);
  return keywords;
});

const isPdf = computed(() => isPdfDocument(props.adpDocument));

const patentPublicationOffice = computed(() =>
  getPatentPublicationOffice(props.adpDocument),
);

const patentIdentifier = computed(() => getPatentIdentifier(props.adpDocument));

const sendViewProps = computed(() => ({
  adpDocument: props.adpDocument,
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: props.feature,
  },
  rankInWholeFeed: props.rankInWholeFeed,
  siteType: getSiteType(props.adpDocument, false),
  contentsContext: props.contentsContext,
}));

const handleArticleClick = async () => {
  adpDocument.value.is_read = true;
  await articleClickedEvent(sendViewProps.value, props.adpDocument);
};
</script>

<template>
  <div
    class="document-card"
    :class="{ 'document-card--narrow': isNarrow }"
    ref="adpDocumentCardRef"
    v-if="adpDocument"
  >
    <VTooltip placement="top" :disabled="hitKeywords.length === 0">
      <div class="document-card__body">
        <div class="document-card__info">
          <div class="document-card__header c-text-regular--s">
            <span class="document-card__doctype c-title-bold--xs">
              <CitationBadge
                :doc-type="adpDocument.doc_type"
                :number="badgeNumber"
              />
            </span>
            <span class="document-card__publisher">{{
              getPublisher(adpDocument)
            }}</span>
            <VTooltip
              placement="bottom"
              :disabled="!isResearchPaper(props.adpDocument)"
            >
              <span
                v-if="
                  isResearchPaper(props.adpDocument) &&
                  props.adpDocument.is_priority_journal
                "
                class="document-card__priority-journal c-text-regular--xs"
                >SJR</span
              >
              <template #popper>
                <div>
                  <a
                    class="help-link"
                    href="https://anews-stockmark.zendesk.com/hc/ja/articles/13717202977689-%E8%AB%96%E6%96%87%E6%A9%9F%E8%83%BD%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6"
                    target="_blank"
                    rel="noopener noreferrer"
                    >SJRスコア</a
                  >の上位に入る影響度の高い学術雑誌です
                </div>
              </template>
            </VTooltip>
            <div
              v-if="
                isArticle(props.adpDocument) && props.adpDocument.login_required
              "
              class="document-card__media-type-label c-title-bold--xs"
            >
              ログインが必要
            </div>
            <div
              v-else-if="props.adpDocument.is_alliance_media"
              class="document-card__media-type-label c-title-bold--xs"
            >
              {{ PRODUCT_NAME }}で読める有料記事
            </div>
          </div>

          <div class="document-card__content-wrapper">
            <div class="content">
              <DocumentTitle
                :adp-document="adpDocument"
                :max-lines="2"
                @article-link-clicked="handleArticleClick"
                @article-link-context-menu="
                  articleContextMenuClickedEvent(
                    sendViewProps,
                    $event,
                    adpDocument,
                  )
                "
              />
            </div>
            <div class="document-card__image">
              <AdpDocumentCardLink
                :adp-document="adpDocument"
                @article-link-clicked="handleArticleClick"
                @article-link-context-menu="
                  articleContextMenuClickedEvent(
                    sendViewProps,
                    $event,
                    adpDocument,
                  )
                "
              >
                <img
                  :src="adpDocument.image_url ?? 'noImage1to1'"
                  @error="imageErrorHandler($event, '1to1')"
                  loading="lazy"
                />
              </AdpDocumentCardLink>
            </div>
          </div>

          <div
            v-if="isPdf && isResearchPaper(adpDocument)"
            class="document-card__provided-site document-card__info__no-gap c-text-regular--s"
          >
            <AdpDocumentCardLink
              :adp-document="adpDocument"
              :use-pdf-url="false"
              @article-link-clicked="handleArticleClick"
              @article-link-context-menu="
                articleContextMenuClickedEvent(
                  sendViewProps,
                  $event,
                  adpDocument,
                )
              "
              >提供サイトで見る</AdpDocumentCardLink
            >
          </div>
          <div class="document-card__sub-info">
            <div class="document-card__date c-text-regular--s">
              {{ formatDate(adpDocument.published_at) }}
            </div>
            <div v-if="patentPublicationOffice" class="c-text-regular--s">
              ・{{ patentPublicationOffice }}
            </div>
            <div v-if="patentIdentifier" class="c-text-regular--s">
              ・{{ patentIdentifier }}
            </div>
          </div>
        </div>
      </div>
      <template #popper>
        <span>含むキーワード</span>
        <div v-for="(keyword, keywordIndex) in hitKeywords" :key="keywordIndex">
          ・{{ keyword }}
        </div>
      </template>
    </VTooltip>
    <div class="spacing-16"></div>
    <div class="document-card__user-action">
      <AdpDocumentActionBar
        :adp-document="adpDocument"
        :is-marked="isMarked"
        :marked-users="markedUsers"
        :marked-groups="markedGroups"
        :commented-users="commentedUsers"
        :option-actions="optionActions"
        @option-action="handleOptionAction"
        @handle-popup-menu-click="handlePopupMenu"
        :mark-handler="handleMarkButton"
        @handle-group-mark-click="handleGroupMarkButton"
        @handle-comment-click="handleCommentButton"
      />
    </div>
    <MarkModal
      v-if="actionModal === 'mark'"
      :adp-document="adpDocument"
      :page-name="pageName"
      :feature="feature"
      @submit="handleMarkButton"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-if="actionModal === 'group_mark'"
      :adp-document="adpDocument"
      :page-name="pageName"
      :feature="feature"
      @submit="groupMarkArticle"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-if="actionModal === 'comment'"
      :adp-document="adpDocument"
      :page-name="pageName"
      :feature="feature"
      :is-posting-comment="isPostingComment"
      @submit="commentArticle"
      @close="closeActionModal"
    ></CommentCreateModal>
  </div>
</template>

<style scoped lang="scss">
.document-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  padding: 16px 16px 4px;
  background-color: white;
  border: 1px solid $color-gray400;
  border-radius: 4px;

  &--narrow {
    grid-template-columns: 1fr;
  }

  &__info {
    display: grid;
    gap: 4px;
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__doctype {
    margin-right: 6px;
  }

  &__publisher {
    color: $color-gray800;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
  }

  &__priority-journal {
    align-self: center;
    height: 16px;
    margin-left: 2px;
    padding: 0 2px;
    color: $color-white;
    background: $color-priority-journal;
  }

  &__media-type-label {
    margin-left: 4px;
    padding: 1px 8px;
    color: $color-gray800;
    background-color: $color-gray200;
    white-space: nowrap;
    border-radius: 2px;
  }

  &__content-wrapper {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 16px;
  }

  &__provided-site {
    text-decoration: underline;
    cursor: pointer;
  }

  &__sub-info {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    color: $color-gray800;
    align-items: center;
  }

  &__date {
    color: $color-gray800;
    margin-right: 2px;
    white-space: nowrap;
  }

  &__image {
    img {
      max-width: 48px;
      object-fit: contain;
      aspect-ratio: 1 / 1;
      border-radius: 4px;
    }
  }

  &__user-action {
    margin-top: auto;
  }
}
</style>
